import "./emailReports.css";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ReloadOutlined,
  FilePdfOutlined,
  RedoOutlined,
  WarningOutlined,
  SendOutlined,
  ExperimentOutlined,
  StopOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import {
  collection,
  deleteField,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import dayjs from "dayjs";

import { auth, db, functions } from "../../firebase";
import { GreenNotify, RedNotify, emailFormat, splitEmails } from "../../helper/helper";
import PageHeader from "../../components/pageHeader/PageHeader";

// The Trigger Email extension stamps each mail doc with delivery.state once it
// processes it. Until an email sender (the extension) is configured, docs have
// no delivery field — which we surface as "Queued".
const STATUS_META = {
  SUCCESS: { color: "green", label: "Sent" },
  ERROR: { color: "red", label: "Failed" },
  PROCESSING: { color: "blue", label: "Sending" },
  RETRY: { color: "orange", label: "Retrying" },
  PENDING: { color: "gold", label: "Pending" },
  QUEUED: { color: "default", label: "Queued" },
};

// Order the "Status" column sort by how much attention a report needs
// (failures first), rather than alphabetically.
const STATUS_SORT_ORDER = [
  "ERROR",
  "RETRY",
  "PENDING",
  "PROCESSING",
  "QUEUED",
  "SUCCESS",
];
const statusRank = (status) => {
  const i = STATUS_SORT_ORDER.indexOf(status);
  return i === -1 ? STATUS_SORT_ORDER.length : i;
};

const statusOf = (mail) => mail?.delivery?.state || "QUEUED";

// Map a raw mail doc to a table row. Property NAME is resolved later (at render)
// from the propsMap so it stays fresh when properties load/refresh.
const mapMail = (d) => {
  const m = d.data() || {};
  const meta = m._meta || {};
  const delivery = m.delivery || null;
  // The PDF is the attachment that has a `path` — attachments[0] is the inline
  // brand logo (cid/content, no path), so index 0 would never be the report.
  const pdfUrl =
    (Array.isArray(m?.message?.attachments)
      ? m.message.attachments.find((a) => a && a.path)
      : null)?.path || null;
  return {
    key: d.id,
    recipient: Array.isArray(m.to) ? m.to.join(", ") : m.to || "—",
    cc: Array.isArray(m.cc) ? m.cc.join(", ") : m.cc || "",
    propertyId: meta.propertyId || null,
    createdAt: meta.createdAt || null,
    manual: !!meta.manual,
    status: statusOf(m),
    error: delivery?.error || null,
    pdfUrl,
  };
};

// The live (and "Load older" page) size for the mail log.
const MAIL_PAGE = 100;

const EmailReports = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  // Older mail pages loaded on demand (static — old deliveries are final).
  const [olderRows, setOlderRows] = useState([]);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // Cursors: the last doc of the live page / of the last older page fetched.
  const liveCursorRef = useRef(null);
  const olderCursorRef = useRef(null);
  // Property id -> name, in state so name joins + the missing-email warning
  // stay live and a Refresh re-render updates the table.
  const [propsMap, setPropsMap] = useState({});
  // Properties with no ownerDetails.email — their reports never queue, so we
  // surface them so an admin can add the missing address.
  const [missingEmail, setMissingEmail] = useState([]);
  // The key of the row currently being resent — drives the per-row spinner.
  const [resendingKey, setResendingKey] = useState(null);
  // Resend modal: which row, plus the editable recipient/cc form.
  const [resendRow, setResendRow] = useState(null);
  const [resendForm] = Form.useForm();

  // Review-before-send: the settings flag, the queue of held reports awaiting an
  // admin's send, and per-row/modal state for the test + release actions.
  const [reviewMode, setReviewMode] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [pending, setPending] = useState([]);
  // Reports an admin chose never to send (ownerReportStatus = "dismissed").
  // Kept listed so the decision is visible and reversible (Restore).
  const [dismissed, setDismissed] = useState([]);
  // inspectionId currently being dismissed/restored -> per-row spinner.
  const [dismissingId, setDismissingId] = useState(null);
  // propertyId -> owner email, for the held-report rows + send-to-owner guard.
  const [ownerEmails, setOwnerEmails] = useState({});
  // inspectionId currently sending (test or release) -> drives per-row spinners.
  const [sendingId, setSendingId] = useState(null);
  // Test modal: which held report, plus the editable recipient form.
  const [testRow, setTestRow] = useState(null);
  const [testForm] = Form.useForm();
  // Send-to-owner modal: which held report, plus the editable recipient/cc form.
  const [sendRow, setSendRow] = useState(null);
  const [sendForm] = Form.useForm();

  // One-shot: load properties for name joins + the missing-email warning list.
  const loadProperties = useCallback(async () => {
    const propsSnap = await getDocs(collection(db, "properties"));
    const map = {};
    const owners = {};
    const missing = [];
    propsSnap.docs.forEach((d) => {
      const data = d.data() || {};
      map[d.id] = data.propertyName || "";
      const email = data.ownerDetails && data.ownerDetails.email;
      owners[d.id] = email || "";
      if (!email) {
        missing.push({
          id: d.id,
          name: data.propertyName || d.id,
          owner: (data.ownerDetails && data.ownerDetails.ownerName) || "",
        });
      }
    });
    missing.sort((a, b) => a.name.localeCompare(b.name));
    setPropsMap(map);
    setOwnerEmails(owners);
    setMissingEmail(missing);
  }, []);

  // Subscribe to the RECENT page of the mail collection live so statuses update
  // in real time (Queued -> Sending -> Sent/Failed) without a manual refresh.
  // Bounded (mail is append-only and grows forever): only the newest MAIL_PAGE
  // docs are live; "Load older" appends earlier pages as one-shot reads — their
  // delivery states are long since final, so they don't need to be live.
  // Independent of the property load, so names fill in via propsMap as soon as
  // it resolves.
  useEffect(() => {
    loadProperties().catch((e) => console.error("Error loading properties:", e));
    const unsub = onSnapshot(
      query(
        collection(db, "mail"),
        orderBy("_meta.createdAt", "desc"),
        limit(MAIL_PAGE),
      ),
      (snap) => {
        setRows(snap.docs.map(mapMail));
        liveCursorRef.current = snap.docs[snap.docs.length - 1] || null;
        // Until an older page is loaded, "there's more" = the live page is full.
        if (!olderCursorRef.current) setHasOlder(snap.size === MAIL_PAGE);
        setLoading(false);
      },
      (error) => {
        RedNotify(error?.message || "Failed to load email reports");
        console.error("Error loading email reports:", error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [loadProperties]);

  // One-shot page of mail older than what's already loaded, appended below the
  // live page.
  const loadOlder = useCallback(async () => {
    const cursor = olderCursorRef.current || liveCursorRef.current;
    if (!cursor) return;
    setLoadingOlder(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, "mail"),
          orderBy("_meta.createdAt", "desc"),
          startAfter(cursor),
          limit(MAIL_PAGE),
        ),
      );
      if (snap.docs.length) {
        olderCursorRef.current = snap.docs[snap.docs.length - 1];
      }
      setOlderRows((prev) => [...prev, ...snap.docs.map(mapMail)]);
      setHasOlder(snap.size === MAIL_PAGE);
    } catch (error) {
      RedNotify(error?.message || "Couldn't load older reports");
      console.error("Error loading older reports:", error);
    } finally {
      setLoadingOlder(false);
    }
  }, []);

  // Live: the review-before-send flag.
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "settings", "ownerReports"),
      (snap) => setReviewMode(snap.exists() && snap.data().reviewBeforeSend === true),
      (e) => console.error("Error loading owner-report settings:", e),
    );
    return () => unsub();
  }, []);

  // Live: inspections whose report is rendered but held for admin review.
  useEffect(() => {
    const q = query(
      collection(db, "inspections"),
      where("ownerReportStatus", "==", "pending_review"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            propertyId: data.propertyId || null,
            propertyName: data.propertyName || "",
            pdfUrl: data.reportPdfUrl || null,
            heldAt: data.ownerReportHeldAt || data.completedAt || null,
          };
        });
        items.sort((a, b) => (b.heldAt || "").localeCompare(a.heldAt || ""));
        setPending(items);
      },
      (e) => console.error("Error loading held reports:", e),
    );
    return () => unsub();
  }, []);

  // Live: reports an admin dismissed (never to be sent).
  useEffect(() => {
    const q = query(
      collection(db, "inspections"),
      where("ownerReportStatus", "==", "dismissed"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            propertyId: data.propertyId || null,
            propertyName: data.propertyName || "",
            pdfUrl: data.reportPdfUrl || null,
            dismissedAt: data.ownerReportDismissedAt || null,
          };
        });
        items.sort((a, b) =>
          (b.dismissedAt || "").localeCompare(a.dismissedAt || ""),
        );
        setDismissed(items);
      },
      (e) => console.error("Error loading dismissed reports:", e),
    );
    return () => unsub();
  }, []);

  // Dismiss a held report: it never sends and leaves the review queue. A plain
  // status flip on the inspection (nothing is deleted — the PDF stays), so it's
  // a direct write; the rules restrict inspection updates to admins.
  const dismissReport = useCallback(async (row) => {
    setDismissingId(row.id);
    try {
      await updateDoc(doc(db, "inspections", row.id), {
        ownerReportStatus: "dismissed",
        ownerReportDismissedAt: new Date().toISOString(),
        ownerReportDismissedBy: auth.currentUser?.uid || null,
      });
      GreenNotify("Report dismissed — it won't be sent to the owner");
    } catch (error) {
      RedNotify(error?.message || "Couldn't dismiss the report");
      console.error("Error dismissing report:", error);
    } finally {
      setDismissingId(null);
    }
  }, []);

  // Undo a dismissal: back to the awaiting-review queue.
  const restoreReport = useCallback(async (row) => {
    setDismissingId(row.id);
    try {
      await updateDoc(doc(db, "inspections", row.id), {
        ownerReportStatus: "pending_review",
        ownerReportDismissedAt: deleteField(),
        ownerReportDismissedBy: deleteField(),
      });
      GreenNotify("Report restored to the review queue");
    } catch (error) {
      RedNotify(error?.message || "Couldn't restore the report");
      console.error("Error restoring report:", error);
    } finally {
      setDismissingId(null);
    }
  }, []);

  // Prefill the test-send form (with the signed-in admin's email) once the modal
  // and its Form are actually mounted — see the resend-form note above.
  useEffect(() => {
    if (testRow) {
      testForm.setFieldsValue({ email: auth.currentUser?.email || "" });
    }
  }, [testRow, testForm]);

  // Prefill the send-to-owner form (with the property's owner email) once the
  // modal and its Form are actually mounted — see the resend-form note below.
  useEffect(() => {
    if (sendRow) {
      sendForm.setFieldsValue({ to: sendRow.ownerEmail || "", cc: "" });
    }
  }, [sendRow, sendForm]);

  // Prefill the resend form once the modal (and its Form) is actually mounted —
  // setFieldsValue in the click handler would run before destroyOnClose remounts
  // the fields and get dropped.
  useEffect(() => {
    if (resendRow) {
      resendForm.setFieldsValue({ to: resendRow.recipient, cc: resendRow.cc || "" });
    }
  }, [resendRow, resendForm]);

  // Resolve property names + sort key at render time so a property load/refresh
  // updates the table without waiting for the next mail write. Live page first,
  // then the on-demand older pages — deduped by id, since new arrivals shift
  // the live window over docs an older fetch may have already returned.
  const displayRows = useMemo(() => {
    const liveKeys = new Set(rows.map((r) => r.key));
    return [...rows, ...olderRows.filter((r) => !liveKeys.has(r.key))].map(
      (r) => ({
        ...r,
        property: propsMap[r.propertyId] || r.propertyId || "—",
      }),
    );
  }, [rows, olderRows, propsMap]);

  // Re-queue an owner-report email via the admin-only callable, optionally with
  // a corrected recipient and/or a CC. Clones the original mail doc server-side
  // into a fresh one for the Trigger Email extension to send again.
  const submitResend = useCallback(async () => {
    let values;
    try {
      values = await resendForm.validateFields();
    } catch {
      return; // validation errors are shown inline
    }
    const row = resendRow;
    if (!row) return;
    const to = splitEmails(values.to);
    const cc = splitEmails(values.cc);
    try {
      setResendingKey(row.key);
      const resend = httpsCallable(functions, "resendOwnerReport");
      await resend({ mailId: row.key, to, cc });
      GreenNotify(`Report re-queued for ${to.join(", ")}`);
      setResendRow(null);
    } catch (error) {
      RedNotify(error?.message || "Failed to resend report");
      console.error("Error resending report:", error);
    } finally {
      setResendingKey(null);
    }
  }, [resendForm, resendRow]);

  // Flip review-before-send. When on, completed inspections' owner reports are
  // rendered and held here for review instead of emailing the owner automatically.
  const toggleReviewMode = useCallback(async (next) => {
    setSavingReview(true);
    try {
      await setDoc(
        doc(db, "settings", "ownerReports"),
        { reviewBeforeSend: next, updatedAt: new Date().toISOString() },
        { merge: true },
      );
      GreenNotify(
        next
          ? "Owner reports will now be held for your review before sending."
          : "Owner reports will now send to owners automatically on completion.",
      );
    } catch (error) {
      RedNotify(error?.message || "Couldn't update the setting");
      console.error("Error saving review setting:", error);
    } finally {
      setSavingReview(false);
    }
  }, []);

  // Send a [TEST] copy of a held report to an address the admin enters (prefilled
  // to their own email) — to eyeball it before it goes to the owner.
  const submitTest = useCallback(async () => {
    let values;
    try {
      values = await testForm.validateFields();
    } catch {
      return;
    }
    const row = testRow;
    if (!row) return;
    try {
      setSendingId(row.id);
      const send = httpsCallable(functions, "sendOwnerReport");
      await send({ inspectionId: row.id, to: [values.email.trim()], test: true });
      GreenNotify(`Test report sent to ${values.email.trim()}`);
      setTestRow(null);
    } catch (error) {
      RedNotify(error?.message || "Failed to send test report");
      console.error("Error sending test report:", error);
    } finally {
      setSendingId(null);
    }
  }, [testForm, testRow]);

  // Release a held report to the owner (and mark it sent), to the recipient(s)
  // in the form — prefilled with the owner email, editable, with an optional CC.
  const submitSendToOwner = useCallback(async () => {
    let values;
    try {
      values = await sendForm.validateFields();
    } catch {
      return; // validation errors are shown inline
    }
    const row = sendRow;
    if (!row) return;
    const to = splitEmails(values.to);
    const cc = splitEmails(values.cc);
    try {
      setSendingId(row.id);
      const approve = httpsCallable(functions, "approveOwnerReport");
      await approve({ inspectionId: row.id, to, cc });
      GreenNotify(`Report sent to ${to.join(", ")}`);
      setSendRow(null);
    } catch (error) {
      RedNotify(error?.message || "Failed to send report to owner");
      console.error("Error sending report to owner:", error);
    } finally {
      setSendingId(null);
    }
  }, [sendForm, sendRow]);

  const counts = useMemo(() => {
    const c = { Sent: 0, Failed: 0, Queued: 0, Other: 0 };
    rows.forEach((r) => {
      if (r.status === "SUCCESS") c.Sent += 1;
      else if (r.status === "ERROR") c.Failed += 1;
      else if (r.status === "QUEUED") c.Queued += 1;
      else c.Other += 1;
    });
    return c;
  }, [rows]);

  const columns = [
    {
      title: "Property",
      dataIndex: "property",
      align: "center",
      sorter: (a, b) =>
        (a.property || "")
          .toLowerCase()
          .localeCompare((b.property || "").toLowerCase()),
      render: (name, row) => (
        <span>
          {name}
          {row.manual && (
            <Tooltip title="Sent manually from admin">
              <Tag color="purple" style={{ marginLeft: 6 }}>
                Manual
              </Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "Owner Email",
      dataIndex: "recipient",
      align: "center",
      sorter: (a, b) =>
        (a.recipient || "")
          .toLowerCase()
          .localeCompare((b.recipient || "").toLowerCase()),
    },
    {
      title: "Queued",
      dataIndex: "createdAt",
      align: "center",
      // createdAt is an ISO string, so it sorts chronologically as a string.
      sorter: (a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""),
      render: (v) => (v ? dayjs(v).format("MMM D, h:mm A") : "—"),
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      sorter: (a, b) => statusRank(a.status) - statusRank(b.status),
      render: (status, row) => {
        const meta = STATUS_META[status] || STATUS_META.QUEUED;
        const tag = <Tag color={meta.color}>{meta.label}</Tag>;
        return status === "ERROR" && row.error ? (
          <Tooltip title={row.error}>{tag}</Tooltip>
        ) : (
          tag
        );
      },
    },
    {
      title: "Report",
      dataIndex: "pdfUrl",
      align: "center",
      render: (pdfUrl) =>
        pdfUrl ? (
          <Button
            type="link"
            icon={<FilePdfOutlined />}
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
          >
            View PDF
          </Button>
        ) : (
          <span className="email-reports-muted">No PDF</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, row) => {
        const canResend = row.recipient && row.recipient !== "—";
        const button = (
          <Button
            type="link"
            icon={<RedoOutlined />}
            loading={resendingKey === row.key}
            disabled={!canResend}
            onClick={() => setResendRow(row)}
          >
            Resend
          </Button>
        );
        return canResend ? (
          button
        ) : (
          <Tooltip title="No recipient on this email">{button}</Tooltip>
        );
      },
    },
  ];

  const pendingColumns = [
    { title: "Property", dataIndex: "property", align: "center" },
    {
      title: "Owner Email",
      dataIndex: "ownerEmail",
      align: "center",
      render: (v) =>
        v || <span className="email-reports-muted">No owner email</span>,
    },
    {
      title: "Held",
      dataIndex: "heldAt",
      align: "center",
      render: (v) => (v ? dayjs(v).format("MMM D, h:mm A") : "—"),
    },
    {
      title: "Report",
      dataIndex: "pdfUrl",
      align: "center",
      render: (pdfUrl) =>
        pdfUrl ? (
          <Button
            type="link"
            icon={<FilePdfOutlined />}
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
          >
            View PDF
          </Button>
        ) : (
          <span className="email-reports-muted">Rendering…</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, row) => (
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            size="small"
            icon={<ExperimentOutlined />}
            loading={sendingId === row.id}
            onClick={() => setTestRow(row)}
          >
            Send test
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            loading={sendingId === row.id}
            onClick={() => setSendRow(row)}
          >
            Send to owner
          </Button>
          <Popconfirm
            title="Dismiss this report?"
            description="The owner won't receive it. You can restore it later."
            okText="Dismiss"
            okButtonProps={{ danger: true }}
            onConfirm={() => dismissReport(row)}
          >
            <Button
              size="small"
              danger
              icon={<StopOutlined />}
              loading={dismissingId === row.id}
            >
              Dismiss
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const dismissedColumns = [
    { title: "Property", dataIndex: "property", align: "center" },
    {
      title: "Dismissed",
      dataIndex: "dismissedAt",
      align: "center",
      render: (v) => (v ? dayjs(v).format("MMM D, h:mm A") : "—"),
    },
    {
      title: "Report",
      dataIndex: "pdfUrl",
      align: "center",
      render: (pdfUrl) =>
        pdfUrl ? (
          <Button
            type="link"
            icon={<FilePdfOutlined />}
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
          >
            View PDF
          </Button>
        ) : (
          <span className="email-reports-muted">—</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, row) => (
        <Button
          size="small"
          icon={<UndoOutlined />}
          loading={dismissingId === row.id}
          onClick={() => restoreReport(row)}
        >
          Restore to review
        </Button>
      ),
    },
  ];

  return (
    <div className="admin-products-main-container email-reports-page">
      <PageHeader
        eyebrow="Delivery"
        title="Email Reports"
        subtitle="Owner inspection-report emails — queued, sent, or failed. Status updates live as the email sender processes each one."
        actions={
          <Button
            icon={<ReloadOutlined />}
            onClick={() =>
              loadProperties().catch((e) => console.error(e))
            }
          >
            Refresh
          </Button>
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>
              Review owner reports before sending
            </div>
            <div className="email-reports-muted" style={{ maxWidth: 620 }}>
              When on, a completed inspection&rsquo;s report is prepared and held
              here for you instead of emailing the owner automatically. Send
              yourself a test to verify it, then release it to the owner.
            </div>
          </div>
          <Switch
            checked={reviewMode}
            loading={savingReview}
            onChange={toggleReviewMode}
            checkedChildren="On"
            unCheckedChildren="Off"
          />
        </div>
      </Card>

      {pending.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Typography.Title level={5} style={{ marginBottom: 8 }}>
            Awaiting review ({pending.length})
          </Typography.Title>
          <div className="server-roles-tb-main-container">
            <Table
              className="subscriptionapi-table"
              rowKey="id"
              columns={pendingColumns}
              dataSource={pending.map((p) => ({
                ...p,
                property:
                  p.propertyName || propsMap[p.propertyId] || p.propertyId || "—",
                ownerEmail: ownerEmails[p.propertyId] || "",
              }))}
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
              rowClassName={(record, index) =>
                index % 2 === 0 ? "server-role-even-row" : "server-role-odd-row"
              }
            />
          </div>
        </div>
      )}

      {dismissed.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Typography.Title level={5} style={{ marginBottom: 8 }}>
            Dismissed ({dismissed.length})
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Reports you chose not to send. The owner never received these;
            restore one to put it back in the review queue.
          </Typography.Paragraph>
          <div className="server-roles-tb-main-container">
            <Table
              className="subscriptionapi-table"
              rowKey="id"
              columns={dismissedColumns}
              dataSource={dismissed.map((p) => ({
                ...p,
                property:
                  p.propertyName || propsMap[p.propertyId] || p.propertyId || "—",
              }))}
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
              rowClassName={(record, index) =>
                index % 2 === 0 ? "server-role-even-row" : "server-role-odd-row"
              }
            />
          </div>
        </div>
      )}

      {missingEmail.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
          message={`${missingEmail.length} ${
            missingEmail.length === 1 ? "property has" : "properties have"
          } no owner email — their reports won't be sent`}
          description={
            <List
              size="small"
              dataSource={missingEmail}
              renderItem={(p) => (
                <List.Item style={{ padding: "4px 0", border: "none" }}>
                  <Typography.Link href={`/properties/${p.id}`}>
                    {p.name}
                  </Typography.Link>
                  {p.owner ? (
                    <span className="email-reports-muted">
                      &nbsp;— {p.owner}
                    </span>
                  ) : null}
                </List.Item>
              )}
            />
          }
        />
      )}

      <div className="email-reports-summary">
        <Tag color="green">Sent: {counts.Sent}</Tag>
        <Tag color="red">Failed: {counts.Failed}</Tag>
        <Tag color="default">Queued: {counts.Queued}</Tag>
        {counts.Other > 0 && <Tag color="blue">In progress: {counts.Other}</Tag>}
      </div>

      {loading ? (
        <div className="email-reports-loading">
          <Spin />
        </div>
      ) : displayRows.length === 0 ? (
        <Empty description="No report emails yet. They appear here when an inspection is completed." />
      ) : (
        <div className="server-roles-tb-main-container">
          <Table
            className="subscriptionapi-table"
            columns={columns}
            dataSource={displayRows}
            pagination={{ pageSize: 12, hideOnSinglePage: true }}
            rowClassName={(record, index) =>
              index % 2 === 0 ? "server-role-even-row" : "server-role-odd-row"
            }
          />
          {hasOlder && (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <Button loading={loadingOlder} onClick={loadOlder}>
                Load older reports
              </Button>
            </div>
          )}
        </div>
      )}

      <Modal
        title="Resend report"
        open={!!resendRow}
        onCancel={() => setResendRow(null)}
        onOk={submitResend}
        okText="Resend"
        confirmLoading={resendingKey !== null}
        destroyOnClose
      >
        <p className="email-reports-muted" style={{ marginTop: 0 }}>
          A fresh copy of this report
          {resendRow && resendRow.property && resendRow.property !== "—"
            ? ` for ${resendRow.property}`
            : ""}{" "}
          will be emailed. Correct the recipient or add a CC if needed — the
          owner will receive it again.
        </p>
        <Form form={resendForm} layout="vertical" requiredMark={false}>
          <Form.Item
            label="Recipient"
            name="to"
            rules={[
              { required: true, message: "Enter a recipient email." },
              () => ({
                validator(_, value) {
                  const list = splitEmails(value);
                  if (list.length && list.every((e) => emailFormat.test(e))) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Enter a valid email."));
                },
              }),
            ]}
          >
            <Input placeholder="owner@example.com" />
          </Form.Item>
          <Form.Item
            label="CC (optional)"
            name="cc"
            extra="Comma-separate multiple addresses."
            rules={[
              () => ({
                validator(_, value) {
                  const list = splitEmails(value);
                  if (!list.length || list.every((e) => emailFormat.test(e))) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("One of the CC addresses looks invalid."),
                  );
                },
              }),
            ]}
          >
            <Input placeholder="office@2ndhomeut.com" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Send report to owner"
        open={!!sendRow}
        onCancel={() => setSendRow(null)}
        onOk={submitSendToOwner}
        okText="Send"
        confirmLoading={!!sendRow && sendingId === sendRow.id}
        destroyOnClose
      >
        <p className="email-reports-muted" style={{ marginTop: 0 }}>
          This releases the held report
          {sendRow && sendRow.property && sendRow.property !== "—"
            ? ` for ${sendRow.property}`
            : ""}{" "}
          and marks it sent. The owner&rsquo;s email is prefilled — edit the
          recipient or add a CC if needed.
        </p>
        <Form form={sendForm} layout="vertical" requiredMark={false}>
          <Form.Item
            label="Recipient"
            name="to"
            rules={[
              { required: true, message: "Enter a recipient email." },
              () => ({
                validator(_, value) {
                  const list = splitEmails(value);
                  if (list.length && list.every((e) => emailFormat.test(e))) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Enter a valid email."));
                },
              }),
            ]}
          >
            <Input placeholder="owner@example.com" />
          </Form.Item>
          <Form.Item
            label="CC (optional)"
            name="cc"
            extra="Comma-separate multiple addresses."
            rules={[
              () => ({
                validator(_, value) {
                  const list = splitEmails(value);
                  if (!list.length || list.every((e) => emailFormat.test(e))) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("One of the CC addresses looks invalid."),
                  );
                },
              }),
            ]}
          >
            <Input placeholder="office@2ndhomeut.com" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Send a test report"
        open={!!testRow}
        onCancel={() => setTestRow(null)}
        onOk={submitTest}
        okText="Send test"
        confirmLoading={!!testRow && sendingId === testRow.id}
        destroyOnClose
      >
        <p className="email-reports-muted" style={{ marginTop: 0 }}>
          A copy of this report
          {testRow && testRow.property && testRow.property !== "—"
            ? ` for ${testRow.property}`
            : ""}{" "}
          will be sent with a &ldquo;[TEST]&rdquo; subject so you can verify it
          before it goes to the owner.
        </p>
        <Form form={testForm} layout="vertical" requiredMark={false}>
          <Form.Item
            label="Send test to"
            name="email"
            rules={[
              { required: true, message: "Enter an email." },
              () => ({
                validator(_, value) {
                  return emailFormat.test((value || "").trim())
                    ? Promise.resolve()
                    : Promise.reject(new Error("Enter a valid email."));
                },
              }),
            ]}
          >
            <Input placeholder="you@2ndhomeut.com" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmailReports;
